<template>
  <section class="sa-rank">
    <div class="sa-rank__head">
      <h3 class="sa-rank__title">{{ title }}</h3>
      <div class="sa-rank__tabs" role="tablist" aria-label="Perspectivas do ranking">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          class="sa-rank__tab"
          :class="{ active: modelValue === tab.id }"
          :aria-selected="modelValue === tab.id"
          :tabindex="modelValue === tab.id ? 0 : -1"
          @click="$emit('update:modelValue', tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div class="sa-rank__table-wrap" role="tabpanel">
      <table class="sa-rank__table">
        <thead>
          <tr>
            <th scope="col" class="sa-rank__col-pos">#</th>
            <th scope="col" class="sa-rank__col-company">Empresa</th>
            <th scope="col" class="sa-rank__col-system">Sistema</th>
            <th scope="col" class="sa-rank__col-value">{{ valueColumnLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="activeTab + '-' + row.id"
            :class="{ 'sa-rank__row--top': index === 0 }"
          >
            <td class="sa-rank__col-pos">
              <span class="sa-rank__pos">{{ String(index + 1).padStart(2, '0') }}</span>
            </td>
            <td class="sa-rank__col-company">
              <span class="sa-rank__name">{{ row.name }}</span>
            </td>
            <td class="sa-rank__col-system">
              <span class="sa-rank__badge" :class="badgeClass(row)">{{ systemLabel(row) }}</span>
            </td>
            <td class="sa-rank__col-value">{{ formatRowValue(row) }}</td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="4" class="sa-rank__empty">Nenhum cliente neste ranking.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { formatCurrencyBRL, formatNumberBR, systemLabel } from '@/utils/superAdmin/formatters';

export default {
  name: 'DashboardRanking',
  props: {
    title: { type: String, default: 'Ranking de clientes' },
    modelValue: { type: String, default: 'revenue' },
    tabs: {
      type: Array,
      default: () => [
        { id: 'revenue', label: 'Faturamento' },
        { id: 'students', label: 'Alunos' },
        { id: 'sales', label: 'Vendas' },
        { id: 'courses', label: 'Cursos' },
      ],
    },
    topByRevenue: { type: Array, default: () => [] },
    topByStudents: { type: Array, default: () => [] },
    topBySales: { type: Array, default: () => [] },
    topByCourses: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  computed: {
    activeTab() {
      return this.modelValue;
    },
    rows() {
      if (this.activeTab === 'students') return this.topByStudents;
      if (this.activeTab === 'sales') return this.topBySales;
      if (this.activeTab === 'courses') return this.topByCourses;
      return this.topByRevenue;
    },
    valueColumnLabel() {
      if (this.activeTab === 'students') return 'Alunos';
      if (this.activeTab === 'sales') return 'Vendas';
      if (this.activeTab === 'courses') return 'Cursos';
      return 'Faturamento';
    },
  },
  methods: {
    systemLabel,
    badgeClass(row) {
      if (row.typeSystem === 2) return 'sa-rank__badge--weflix';
      return 'sa-rank__badge--weeduc';
    },
    formatRowValue(row) {
      if (this.activeTab === 'revenue') return formatCurrencyBRL(row.revenue);
      if (this.activeTab === 'students') return formatNumberBR(row.students);
      if (this.activeTab === 'sales') return formatNumberBR(row.sales);
      return formatNumberBR(row.courses);
    },
  },
};
</script>

<style scoped>
.sa-rank {
  background: var(--dashboard-card, #fff);
  border: 1px solid var(--dashboard-border, #e8eaf0);
  border-radius: 14px;
  padding: 1.15rem 1.2rem 0.5rem;
  min-width: 0;
}

.sa-rank__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem 1rem;
  margin-bottom: 0.85rem;
}

.sa-rank__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--dashboard-text, #191c24);
}

.sa-rank__tabs {
  display: flex;
  gap: 0.35rem;
  overflow-x: auto;
  max-width: 100%;
  padding-bottom: 0.15rem;
  -webkit-overflow-scrolling: touch;
}

.sa-rank__tab {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--dashboard-muted, #697386);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 160ms ease, color 160ms ease;
}

.sa-rank__tab:hover {
  background: #f3f4f8;
  color: var(--dashboard-text, #191c24);
}

.sa-rank__tab.active {
  background: #f0edfa;
  color: var(--dashboard-primary, #250891);
}

.sa-rank__tab:focus-visible {
  outline: 2px solid var(--dashboard-primary, #250891);
  outline-offset: 2px;
}

.sa-rank__table-wrap {
  width: 100%;
  overflow-x: auto;
}

.sa-rank__table {
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
}

.sa-rank__table th {
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dashboard-muted, #697386);
  padding: 0.55rem 0.65rem;
  border-bottom: 1px solid var(--dashboard-border, #e8eaf0);
  white-space: nowrap;
}

.sa-rank__table td {
  padding: 0.7rem 0.65rem;
  border-bottom: 1px solid #f1f2f6;
  font-size: 0.875rem;
  color: var(--dashboard-text, #191c24);
  vertical-align: middle;
  text-align: left;
}

.sa-rank__table tbody tr:last-child td {
  border-bottom: 0;
}

.sa-rank__table tbody tr {
  transition: background-color 150ms ease;
}

.sa-rank__table tbody tr:hover {
  background: #fafbff;
}

.sa-rank__row--top {
  background: #faf9ff;
}

.sa-rank__row--top:hover {
  background: #f5f3fc;
}

.sa-rank__col-pos {
  width: 3.25rem;
  text-align: left;
}

.sa-rank__col-company {
  width: 42%;
  text-align: left;
}

.sa-rank__col-system {
  width: 28%;
  text-align: left;
}

.sa-rank__pos {
  display: inline-block;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 0.8125rem;
  color: var(--dashboard-muted, #697386);
}

.sa-rank__row--top .sa-rank__pos {
  color: var(--dashboard-primary, #250891);
}

.sa-rank__name {
  display: block;
  font-weight: 600;
  text-align: left;
}

.sa-rank__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.sa-rank__badge--weeduc {
  background: #f0edfa;
  color: #250891;
}

.sa-rank__badge--weflix {
  background: #e8f9fb;
  color: #0e8a92;
}

.sa-rank__col-value {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
  width: 8.5rem;
}

.sa-rank__empty {
  text-align: center;
  color: var(--dashboard-muted, #697386);
  padding: 1.5rem 0.65rem;
}
</style>
